import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MatchEvent } from "../../../store/useMatchLogStore";
import {
  ACTION_FLOWS,
  PENALTY_SPOT_STATSBOMB,
  SKIP_RECIPIENT_ID,
} from "../constants";
import { useRefinementChips } from "./useRefinementChips";
import {
  ActionStep,
  EventType,
  FieldAnchor,
  FieldCoordinate,
  IneffectiveAction,
  Match,
  Player,
  Team,
} from "../types";
import { ZONES, ZONE_W, ZONE_H, locationToZoneId } from "../utils/heatMapZones";
import { WOODWORK_TARGET_POINTS, type GoalPlacement } from "../utils/goalFrame";
import type { PenaltyKickOutcome } from "../components/molecules/PenaltyKickScreen";

export type PositionMode = "manual" | "auto";

interface UseActionFlowParams {
  match: Match | null;
  matchClock: string;
  operatorPeriod: number;
  selectedTeam: "home" | "away" | "both";
  isSubmitting: boolean;
  cardYellowCounts: Record<string, number>;
  expelledPlayerIds: Set<string>;
  recentEvents: MatchEvent[];
  /** EPIC-98 ("Tiro Penal" goal-frame screen): `handlePenaltyKickDispatch`
   * needs to know whether the OPPONENT's goalkeeper is actually on the
   * pitch before it can auto-credit an Atajado save — the same on-field
   * source `ActionStage.tsx`'s (now-retired-for-Penalty) taker-candidate
   * lookup already used. Threaded in from `CockpitContext.tsx` (out-of-band
   * addition, same precedent as `handleWoodworkPlace`'s own threading
   * comment there — see team_findings.md). */
  onFieldIds: { home: Set<string>; away: Set<string> };
  onIneffectiveTrigger?: (payload: {
    note?: string;
    teamId: string;
    playerId?: string;
    actionType: IneffectiveAction;
  }) => void;
  sendEvent: (event: Omit<MatchEvent, "match_id" | "timestamp">) => void;
}

const resolveEventType = (action: string): EventType => {
  if (action === "Pass") return "Pass";
  if (
    action === "Shot" ||
    action === "Goal" ||
    action === "DirectShot" ||
    action === "OwnGoal" ||
    action === "PenaltyKick" ||
    // Client wave 2026-09-19 ("Tiro al palo"): dispatches as a plain Shot,
    // outcome "Post", with `data.goal_placement` set directly by
    // `handleWoodworkPlace` below — same mapping precedent as PenaltyKick.
    action === "Woodwork"
  )
    return "Shot";
  if (action === "Header") return "Header";
  if (action === "Duel" || action === "DuelWon") return "Duel";
  if (action === "Foul") return "FoulCommitted";
  if (action === "Card") return "Card";
  if (action === "Interception") return "Interception";
  if (action === "Clearance") return "Clearance";
  if (action === "Block") return "Block";
  if (action === "Recovery") return "Recovery";
  if (action === "Carry") return "Carry";
  if (action === "Offside") return "Offside";
  if (
    [
      "Corner",
      "Free Kick",
      "Throw-in",
      "Goal Kick",
      "Penalty",
      "Kick Off",
    ].includes(action)
  ) {
    return "SetPiece";
  }
  if (["Save", "Claim", "Punch", "Pick Up", "Smother"].includes(action)) {
    return "GoalkeeperAction";
  }
  return "Pass";
};

const getActionConfig = (action?: string | null) => {
  if (!action) return undefined;
  return Object.values(ACTION_FLOWS).find((config) =>
    config.actions.includes(action),
  );
};

const buildEventPayload = (
  action: string,
  outcome: string | null,
  recipient: Player | null,
  currentTeam: Team,
  selectedPlayer: Player,
  matchClock: string,
  operatorPeriod: number,
  location?: [number, number],
  endLocation?: [number, number],
  extraData?: Record<string, any>,
  zoneId?: number | null,
): Omit<MatchEvent, "match_id" | "timestamp"> => {
  const eventType = resolveEventType(action);

  const eventData: Omit<MatchEvent, "match_id" | "timestamp"> = {
    match_clock: matchClock,
    period: operatorPeriod,
    team_id: currentTeam.id,
    player_id: selectedPlayer.id,
    type: eventType,
    data: {},
  };

  if (location) {
    eventData.location = location;
  }

  switch (eventType) {
    case "Pass":
      eventData.data = {
        pass_type: "Standard",
        outcome: outcome || "Complete",
        receiver_id: recipient?.id,
        receiver_name: recipient?.full_name,
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Shot":
      eventData.data = {
        shot_type: "Standard",
        outcome: outcome || "OnTarget",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Header":
      eventData.data = {
        shot_type: "Header",
        outcome: outcome || "OnTarget",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Duel":
      eventData.data = {
        duel_type: "Ground",
        outcome: outcome || "Won",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "FoulCommitted":
      eventData.data = {
        foul_type: "Standard",
        outcome: outcome || "Standard",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Card":
      eventData.data = {
        card_type: outcome || "Yellow",
        reason: "Foul",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Interception":
      eventData.data = {
        outcome: outcome || "Success",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Clearance":
      eventData.data = {
        outcome: outcome || "Success",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Block":
      eventData.data = {
        block_type: "Shot",
        outcome: outcome || "Success",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Recovery":
      eventData.data = {
        recovery_type: outcome || "Loose Ball",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Carry":
      eventData.data = {
        carry_type: "Standard",
        outcome: outcome || "Successful",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "Offside":
      eventData.data = {
        pass_player_id: null,
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "SetPiece":
      eventData.data = {
        set_piece_type: action,
        outcome: outcome || "Complete",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    case "GoalkeeperAction":
      eventData.data = {
        action_type: action,
        outcome: outcome || "Success",
        ...(endLocation ? { end_location: endLocation } : {}),
      };
      break;
    default:
      break;
  }

  if (extraData) {
    eventData.data = { ...eventData.data, ...extraData };
  }

  if (zoneId != null) {
    eventData.data.zone_id = zoneId;
  }

  return eventData;
};

export const useActionFlow = ({
  match,
  matchClock,
  operatorPeriod,
  selectedTeam,
  // PMA-COCK-147 (cockpit ack-latency wave): dispatchEvent used to
  // `if (isSubmitting) return false;` here -- a pending ack from an
  // EARLIER event silently swallowed a brand-new, legitimate tap (callers
  // like `handleOutcomeClick` do `if (dispatchEvent(...)) resetFlow();`,
  // so a `false` return just no-ops with no user-visible feedback). Kept in
  // the param list (still passed from CockpitContext.tsx) but no longer
  // read: `resetFlow()` already runs synchronously right after a
  // successful dispatch, before any network round-trip, which is what
  // makes NOT gating on it safe -- the flow is back at `selectPlayer`
  // before a rapid-fire next tap can land, so nothing is ever the same
  // logical event resubmitted. See useMatchSocket.ts's ACK_TIMEOUT_MS/
  // expireAck for the matching backstop against a lost ack getting stuck.
  isSubmitting: _isSubmitting,
  cardYellowCounts,
  expelledPlayerIds,
  recentEvents,
  onIneffectiveTrigger,
  sendEvent,
  onFieldIds,
}: UseActionFlowParams) => {
  const {
    chipState,
    handleChipTap,
    dismissChipPanel,
    triggerChipForAction,
    handlePlacementTap,
    placedPlacement,
  } = useRefinementChips({ recentEvents });

  const [currentStep, setCurrentStep] = useState<ActionStep>("selectPlayer");
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [pendingOutcome, setPendingOutcome] = useState<string | null>(null);
  const [fieldAnchor, setFieldAnchor] = useState<FieldAnchor | null>(null);
  const [selectedPlayerLocation, setSelectedPlayerLocation] = useState<
    [number, number] | null
  >(null);
  /** Exact StatsBomb coords from the player-node click (before zone quantisation). */
  const [playerClickLocation, setPlayerClickLocation] = useState<
    [number, number] | null
  >(null);
  /** Zone the operator confirmed – attached as data.zone_id on every event. */
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  /** Position assignment mode: manual (zone selector) or auto (use node coords). */
  const [positionMode, setPositionMode] = useState<PositionMode>("auto");
  const currentStepRef = useRef<ActionStep>("selectPlayer");

  /** Switch position mode; when going from auto→manual mid-flow, re-show the zone selector. */
  const handlePositionModeChange = useCallback(
    (mode: PositionMode) => {
      setPositionMode(mode);
      if (
        mode === "manual" &&
        selectedPlayer &&
        currentStepRef.current !== "selectPlayer" &&
        currentStepRef.current !== "selectZone"
      ) {
        setSelectedZoneId(null);
        setSelectedPlayerLocation(null);
        setCurrentStep("selectZone");
      }
    },
    [selectedPlayer],
  );

  const currentTeam = useMemo<Team | undefined>(() => {
    if (!match) return undefined;
    if (selectedPlayer) {
      const isHomePlayer = match.home_team.players.some(
        (player) => player.id === selectedPlayer.id,
      );
      if (isHomePlayer) return match.home_team;
      const isAwayPlayer = match.away_team.players.some(
        (player) => player.id === selectedPlayer.id,
      );
      if (isAwayPlayer) return match.away_team;
    }
    if (selectedTeam === "home") return match.home_team;
    if (selectedTeam === "away") return match.away_team;
    return undefined;
  }, [match, selectedPlayer, selectedTeam]);

  const resolvePlayerSide = useCallback(
    (player?: Player | null): "home" | "away" | null => {
      if (!match || !player) return null;
      if (match.home_team.players.some((p) => p.id === player.id))
        return "home";
      if (match.away_team.players.some((p) => p.id === player.id))
        return "away";
      return null;
    },
    [match],
  );

  /**
   * PMA-COCK-181: the destination-step "attacked" goal edge, in CANONICAL
   * space — the SAME derivation `handleDestinationClick`'s own
   * `opponentGoalEdge` local uses for the generic Shot/Goal/Header branch
   * below (home -> "right", away -> "left"), lifted into a memo so
   * `ActionStage.tsx` can read it before any tap happens (to decide where
   * to render the on-target chip). Deliberately NOT flip-adjusted — this
   * hook has no `flipSides` param, and the canonical value is exactly what
   * `handleDestinationClick`/`TacticalField.tsx`'s `resolveGoalMouth` both
   * already compare against, un-flipped. `ActionStage.tsx` (which already
   * receives `manualFieldFlip`) converts this to the DISPLAY-space edge
   * `TacticalField.tsx`'s `onTargetChipEdge` prop expects, mirroring how
   * player-token positions are flipped for display while the canonical
   * attack direction itself never changes.
   */
  const attackedGoalMouthEdge = useMemo<"left" | "right" | null>(() => {
    const side = resolvePlayerSide(selectedPlayer);
    if (side === "home") return "right";
    if (side === "away") return "left";
    return null;
  }, [resolvePlayerSide, selectedPlayer]);

  const resolveOpponentTeamId = useCallback(
    (teamId?: string | null): string | null => {
      if (!match || !teamId) return null;
      if (teamId === match.home_team.id) return match.away_team.id;
      if (teamId === match.away_team.id) return match.home_team.id;
      return null;
    },
    [match],
  );

  const availableActions = useMemo(() => {
    if (!selectedPlayer) return [];
    return Object.values(ACTION_FLOWS).flatMap((config) => config.actions);
  }, [selectedPlayer]);

  const availableOutcomes = useMemo(() => {
    if (!selectedAction) return [];
    return getActionConfig(selectedAction)?.outcomes?.[selectedAction] || [];
  }, [selectedAction]);

  const resetFlow = useCallback(() => {
    setCurrentStep("selectPlayer");
    setSelectedPlayer(null);
    setSelectedAction(null);
    setPendingOutcome(null);
    setFieldAnchor(null);
    setSelectedPlayerLocation(null);
    setPlayerClickLocation(null);
    setSelectedZoneId(null);
  }, []);

  const dispatchEvent = useCallback(
    (
      action: string,
      outcome: string | null,
      recipient: Player | null,
      opts?: {
        location?: [number, number];
        endLocation?: [number, number];
        extraData?: Record<string, any>;
      },
    ) => {
      if (!match || !currentTeam || !selectedPlayer) return false;
      if (expelledPlayerIds.has(selectedPlayer.id)) return false;
      let resolvedOutcome = outcome;
      const sendPayload = (
        nextAction: string,
        nextOutcome: string | null,
        nextRecipient: Player | null,
        extraData?: Record<string, any>,
      ) => {
        const payload = buildEventPayload(
          nextAction,
          nextOutcome,
          nextRecipient,
          currentTeam,
          selectedPlayer,
          matchClock,
          operatorPeriod,
          opts?.location,
          opts?.endLocation,
          extraData ?? opts?.extraData,
          selectedZoneId,
        );
        sendEvent(payload);
      };

      if (action === "Card") {
        const requested = outcome || "Yellow";
        if (requested === "Yellow") {
          const previousYellows = cardYellowCounts[selectedPlayer.id] || 0;
          resolvedOutcome = previousYellows >= 1 ? "Yellow (Second)" : "Yellow";
        } else {
          resolvedOutcome = requested;
        }

        sendPayload("Card", resolvedOutcome, null);

        if (resolvedOutcome === "Yellow (Second)") {
          sendPayload("Card", "Red", null);
        }

        return true;
      }

      const payload = buildEventPayload(
        action,
        resolvedOutcome,
        recipient,
        currentTeam,
        selectedPlayer,
        matchClock,
        operatorPeriod,
        opts?.location,
        opts?.endLocation,
        opts?.extraData,
        selectedZoneId,
      );
      sendEvent(payload);

      if (payload.type === "Shot" || payload.type === "Duel") {
        triggerChipForAction(payload.type as "Shot" | "Duel");
      }

      if (action === "Foul") {
        onIneffectiveTrigger?.({
          teamId: resolveOpponentTeamId(currentTeam.id) || currentTeam.id,
          playerId: selectedPlayer.id,
          actionType: "FreeKick", // Foul → Tiros libres (free kick)
        });
      }
      if (action === "Offside") {
        onIneffectiveTrigger?.({
          teamId: resolveOpponentTeamId(currentTeam.id) || currentTeam.id,
          playerId: selectedPlayer.id,
          actionType: "Other", // Offside no longer has its own category
        });
      }
      return true;
    },
    [
      match,
      currentTeam,
      selectedPlayer,
      selectedZoneId,
      cardYellowCounts,
      expelledPlayerIds,
      recentEvents,
      onIneffectiveTrigger,
      resolveOpponentTeamId,
      matchClock,
      operatorPeriod,
      sendEvent,
      triggerChipForAction,
    ],
  );

  const handlePlayerClick = useCallback(
    (player: Player, anchor?: FieldAnchor, location?: [number, number]) => {
      if (expelledPlayerIds.has(player.id)) {
        return;
      }
      dismissChipPanel();
      setSelectedPlayer(player);
      // Store the exact position from the player node; zone selection will
      // decide whether to keep it (if it falls inside the chosen zone) or
      // fall back to the zone centre.
      setPlayerClickLocation(location ?? null);
      setSelectedPlayerLocation(null);
      setFieldAnchor(anchor ?? null);

      if (positionMode === "auto" && location) {
        // Auto mode: derive zone from player node coords, skip zone selector.
        const autoZoneId = locationToZoneId(location[0], location[1]);
        setSelectedZoneId(autoZoneId);
        setSelectedPlayerLocation(location);
        setCurrentStep(anchor ? "selectQuickAction" : "selectAction");
      } else {
        setCurrentStep("selectZone");
      }
    },
    [expelledPlayerIds, positionMode, dismissChipPanel],
  );

  const handleZoneSelect = useCallback(
    (zoneId: number) => {
      const zone = ZONES[zoneId];
      if (!zone) return;

      // Zone-biased positioning: use the player's exact click location when
      // it falls inside the zone the operator confirmed.  Otherwise, fall
      // back to the zone centre (the operator deliberately corrected the
      // position).
      let location: [number, number];
      if (
        playerClickLocation &&
        playerClickLocation[0] >= zone.x0 &&
        playerClickLocation[0] <= zone.x1 &&
        playerClickLocation[1] >= zone.y0 &&
        playerClickLocation[1] <= zone.y1
      ) {
        location = playerClickLocation;
      } else {
        location = [zone.x0 + ZONE_W / 2, zone.y0 + ZONE_H / 2];
      }

      setSelectedZoneId(zoneId);
      setSelectedPlayerLocation(location);
      setCurrentStep(fieldAnchor ? "selectQuickAction" : "selectAction");
    },
    [fieldAnchor, playerClickLocation],
  );

  const handleQuickActionSelect = useCallback(
    (action: string) => {
      setSelectedAction(action);
      setPendingOutcome(null);
      if (action === "Card") {
        setCurrentStep("selectOutcome");
        return;
      }
      // PMA-COCK-176/177 (user decision 2026-09-09): DirectShot stays
      // destination-driven — no branch here at all (it falls through to the
      // same `setCurrentStep("selectDestination")` default every other
      // unmatched quick action reaches, same as Pass/Foul today). The
      // `shot_type: "Direct"` tag still attaches at DISPATCH time, once the
      // destination tap resolves an outcome — see `handleDestinationClick`'s
      // DirectShot branch below, now on-target-only (Saved/Blocked/OnTarget,
      // never OffTarget). "Shot Out" — retired by PMA-COCK-174, restored by
      // this UC — keeps its own separate instant-dispatch branch below.
      // ── Goal quick action (EPIC-94, AC1, client request 2026-09-23) ────
      // Used to dispatch instantly here, with `location` set to the
      // selected player's token position (never where the shot actually
      // came from). Now falls through to the same
      // `setCurrentStep("selectDestination")` default every other
      // destination-driven quick action reaches (Pass/Foul/DirectShot/
      // Shot Out) — the pitch opens, a tap becomes the shot's `location`
      // with `data.location_source: "tap"` (`handleDestinationClick`'s own
      // early "Goal" branch below), and a visible "Omitir" control
      // (`handleOutcomeSelect("Goal")`, `ActionStage.tsx`) dispatches
      // without a tap, keeping the token position exactly as before (no
      // `location_source` tag — see `handleOutcomeClick`'s own "Goal"
      // branch). The own-goal quick action ("OwnGoal" below) is
      // deliberately unchanged.
      if (action === "Offside") {
        if (selectedPlayer && currentTeam) {
          const sent = dispatchEvent(action, "Standard", null, {
            location: selectedPlayerLocation ?? undefined,
          });
          if (sent) {
            resetFlow();
          }
        }
        return;
      }
      if (action === "Corner") {
        if (selectedPlayer && currentTeam) {
          const awardedCornerTeamId =
            resolveOpponentTeamId(currentTeam.id) || currentTeam.id;
          const payload = buildEventPayload(
            action,
            "Complete",
            null,
            currentTeam,
            selectedPlayer,
            matchClock,
            operatorPeriod,
            selectedPlayerLocation ?? undefined,
            undefined,
            {
              source_team_id: currentTeam.id,
              source_player_id: selectedPlayer.id,
            },
            selectedZoneId,
          );
          payload.team_id = awardedCornerTeamId;
          sendEvent(payload);

          if (onIneffectiveTrigger) {
            onIneffectiveTrigger?.({
              note: "Corner kick",
              teamId: awardedCornerTeamId,
              playerId: selectedPlayer.id,
              actionType: "Corner",
            });
          }
          resetFlow();
        }
        return;
      }
      if (action === "Throw-in") {
        if (selectedPlayer && currentTeam) {
          const sent = dispatchEvent(action, "Complete", null, {
            location: selectedPlayerLocation ?? undefined,
          });
          if (sent) {
            onIneffectiveTrigger?.({
              note: "Throw-in",
              teamId: resolveOpponentTeamId(currentTeam.id) || currentTeam.id,
              playerId: selectedPlayer.id,
              actionType: "ThrowIn",
            });
            resetFlow();
          }
        }
        return;
      }
      // PMA-COCK-176 (user decision 2026-09-09, overruling PMA-COCK-174's
      // retirement): "Shot Out" is restored, byte-for-byte as it was before
      // that UC — instant dispatch, Shot/OffTarget with the out-of-bounds
      // flags, and the same GoalKick ineffective window. DirectShot's own
      // destination-tap flow (handleDestinationClick's DirectShot branch)
      // is unaffected and stays on-target-only.
      // PMA-COCK-184: `shot_type: "Direct"` is now tagged here too — the
      // only field change — so an off-target direct shot is distinguishable
      // from a generic missed Shot and becomes placement-eligible
      // (`isPlacementEligible`). Every `shot_type` consumer (`usePlayerStats
      // .ts`'s penalty count, the server aggregates/xG) matches on
      // `shot_type === "Penalty"` or a literal "penalty"/"pen" technique
      // only, so "Direct" here falls through exactly like the prior
      // implicit "Standard" default — verified before this change, see
      // team_findings.md.
      if (action === "Shot Out") {
        if (selectedPlayer && currentTeam) {
          const sent = dispatchEvent("Shot", "OffTarget", null, {
            location: selectedPlayerLocation ?? undefined,
            extraData: {
              shot_type: "Direct",
              destination_type: "out_of_bounds",
              out_of_bounds: true,
            },
          });
          if (sent) {
            onIneffectiveTrigger?.({
              note: "Shot out of bounds",
              teamId: resolveOpponentTeamId(currentTeam.id) || currentTeam.id,
              playerId: selectedPlayer.id,
              actionType: "GoalKick", // shot wide/off target → defending team takes goal kick
            });
            resetFlow();
          }
        }
        return;
      }
      if (action === "Goal Kick") {
        if (selectedPlayer && currentTeam) {
          const sent = dispatchEvent(action, "Complete", null, {
            location: selectedPlayerLocation ?? undefined,
          });
          if (sent) {
            onIneffectiveTrigger?.({
              note: "Goal kick",
              teamId: resolveOpponentTeamId(currentTeam.id) || currentTeam.id,
              playerId: selectedPlayer.id,
              actionType: "GoalKick",
            });
            resetFlow();
          }
        }
        return;
      }
      // ── Penalty quick action ──────────────────────────────────────────
      // Two-player flow: committer → select fouled opponent → dispatch
      if (action === "Penalty") {
        if (selectedPlayer && currentTeam) {
          setPendingOutcome("Complete");
          setSelectedAction("Penalty");
          setCurrentStep("selectRecipient");
        }
        return;
      }
      // ── PenaltyKick quick action (EPIC-98, "Tiro Penal") ────────────────
      // Its own wheel segment again (dated reversal of PMA-COCK-216's merge
      // into "Penalty" — see `handleRecipientClick`'s Penalty branch's own
      // doc comment). No destination tap — straight to the goal-frame
      // screen (`PenaltyKickScreen.tsx`, rendered by `ActionStage.tsx` at
      // this same "selectOutcome" waypoint, same pattern Woodwork already
      // established below). The TAPPED player (`selectedPlayer`) is the
      // taker, pre-selected but changeable on that screen — never a
      // fouled-player default the way the old merged flow pre-filled it.
      // `handlePenaltyKickDispatch` (not `handleOutcomeClick`) dispatches
      // the actual Shot once the operator confirms an outcome there.
      if (action === "PenaltyKick") {
        if (selectedPlayer && currentTeam) {
          setCurrentStep("selectOutcome");
        }
        return;
      }
      // ── Woodwork quick action (client wave 2026-09-19, "Tiro al palo") ──
      // No destination tap — straight to the reduced three-target frame
      // (`WoodworkTargetSelector`, `ActionStage.tsx`, rendered in place of
      // the plain-text `OutcomeSelectionPanel` for this one action, same
      // `currentStep` waypoint PenaltyKick already uses above).
      // `handleWoodworkPlace` below dispatches once the operator taps a
      // target.
      if (action === "Woodwork") {
        if (selectedPlayer && currentTeam) {
          setCurrentStep("selectOutcome");
        }
        return;
      }
      // ── Own Goal quick action ─────────────────────────────────────────
      if (action === "OwnGoal") {
        if (selectedPlayer && currentTeam) {
          const payload = buildEventPayload(
            "Shot",
            "Goal",
            null,
            currentTeam,
            selectedPlayer,
            matchClock,
            operatorPeriod,
            selectedPlayerLocation ?? undefined,
            undefined,
            { own_goal: true, shot_type: "OwnGoal" },
            selectedZoneId,
          );
          // Attribute goal to opponent team
          const opponentTeamId =
            resolveOpponentTeamId(currentTeam.id) || currentTeam.id;
          payload.team_id = opponentTeamId;
          sendEvent(payload);
          // EPIC-97 (AC3, dated reversal of EPIC-96's "own goals stay out of
          // the goal frame"): this branch bypasses `dispatchEvent` entirely
          // (it needs to reattribute `payload.team_id` to the opponent,
          // which `dispatchEvent` always overwrites with `currentTeam.id`),
          // so it must call `triggerChipForAction` itself — exactly what
          // `dispatchEvent` already does for every other Shot/Duel dispatch
          // above — so the post-dispatch goal-frame overlay picks up this
          // own-goal event once it confirms. `isPlacementEligible`
          // (goalFrame.ts) already accepts an own goal's `outcome: "Goal"`;
          // `ActionStage.tsx`'s `showGoalPlacementOverlay` gate has a
          // matching `data.own_goal === true` OR-branch. The dispatch itself
          // (above) is unchanged and already happened — the overlay's own
          // Skip button only skips the PLACEMENT, never un-dispatches this
          // event, same "skip still dispatches" shape as "Gol"'s own Omitir.
          triggerChipForAction("Shot");
          onIneffectiveTrigger?.({
            note: "Own goal",
            teamId: opponentTeamId,
            playerId: selectedPlayer.id,
            actionType: "Goal",
          });
          resetFlow();
        }
        return;
      }
      // ── Duel Won quick action ─────────────────────────────────────────
      if (action === "DuelWon") {
        if (selectedPlayer && currentTeam) {
          const sent = dispatchEvent("Duel", "Won", null, {
            location: selectedPlayerLocation ?? undefined,
          });
          if (sent) {
            // Now ask operator to select the opponent who lost the duel
            setPendingOutcome("Lost");
            setSelectedAction("Duel");
            setCurrentStep("selectRecipient");
          }
        }
        return;
      }

      setCurrentStep("selectDestination");
    },
    [
      currentTeam,
      matchClock,
      dispatchEvent,
      onIneffectiveTrigger,
      operatorPeriod,
      resetFlow,
      resolveOpponentTeamId,
      selectedZoneId,
      selectedPlayer,
      selectedPlayerLocation,
      sendEvent,
      triggerChipForAction,
    ],
  );

  const handleOpenMoreActions = useCallback(() => {
    setCurrentStep("selectAction");
  }, []);

  const handleActionClick = useCallback((action: string) => {
    setSelectedAction(action);
    setPendingOutcome(null);
    // Shot/Pass use field-based destination selection; others use outcome panel
    if (action === "Shot" || action === "Pass") {
      setCurrentStep("selectDestination");
    } else {
      setCurrentStep("selectOutcome");
    }
  }, []);

  const handleDestinationClick = useCallback(
    (payload: {
      destination: FieldCoordinate;
      targetPlayer?: Player | null;
    }) => {
      if (!selectedAction || !selectedPlayer || !currentTeam) {
        return { sent: false, outOfBounds: false, isGoal: false };
      }

      const destination = payload.destination;
      const targetPlayer = payload.targetPlayer ?? null;
      const selectedPlayerSide = resolvePlayerSide(selectedPlayer);
      const targetPlayerSide = resolvePlayerSide(targetPlayer);
      const isSameTeam =
        selectedPlayerSide && targetPlayerSide
          ? selectedPlayerSide === targetPlayerSide
          : false;
      const isOpponent =
        selectedPlayerSide && targetPlayerSide
          ? selectedPlayerSide !== targetPlayerSide
          : false;

      // PMA-COCK-181 (client ask 2026-09-10; dated reversal EPIC-96,
      // 2026-09-24): a tap on the on-target hint-bar button
      // (`field-ontarget-btn`, ActionStage.tsx — the interactive on-pitch
      // `tactical-ontarget-chip` this branch used to key off was retired
      // outright, replaced by this off-pitch button plus a non-interactive
      // shot guide/tap-ripple on the pitch) always dispatches OnTarget with
      // NO keeper/blocker credit — the client's whole point is a safe way
      // to log a shot that stays on target without crediting a nearby
      // opponent (e.g. a post rebound that reaches a defender, who the
      // client will never tap for fear of logging "remate bloqueado").
      // Checked BEFORE the targetPlayer-driven credit logic below (Foul's
      // own branch included) so this dispatch can never fall through to it
      // — the hint-bar button's own click handler never passes a
      // targetPlayer alongside `onTargetChip` today, but this ordering
      // makes that a structural guarantee, not an incidental one.
      // `shot_type: "Direct"` is still set for DirectShot (unchanged from
      // the normal branch below); `location`/`endLocation` come from the
      // SAME `selectedPlayerLocation`/`destination.statsbomb` pair every
      // other branch here uses, so the shot's end location is the fixed
      // coordinate `buildOnTargetChipCoordinate` (`TacticalField.tsx`)
      // resolves for the button's own goal-mouth edge — no keeper-avoid
      // offset any more, since nothing renders on the pitch at this
      // coordinate for a keeper token to need avoiding.
      if (
        destination.onTargetChip &&
        (selectedAction === "DirectShot" ||
          selectedAction === "Shot" ||
          selectedAction === "Header")
      ) {
        const extraData: Record<string, any> = {};
        if (selectedAction === "DirectShot") {
          extraData.shot_type = "Direct";
        }
        const sent = dispatchEvent(selectedAction, "OnTarget", null, {
          location: selectedPlayerLocation ?? undefined,
          endLocation: destination.statsbomb,
          extraData,
        });
        if (sent) {
          resetFlow();
        }
        return {
          sent,
          outOfBounds: false,
          isGoal: false,
          triggerContext: null,
        };
      }

      // PMA-COCK-166 (client wave 2026-09-07): Foul is a QUICK_ACTIONS entry
      // (no outcome step — see handleQuickActionSelect's default fallthrough
      // to selectDestination), so its field-click destination is the ONLY
      // dispatch path most operators ever use for it; it must honor the
      // same fouled-player step as the generic ActionSelectionPanel path
      // (handleOutcomeClick's needsRecipient check), or criterion 9 would
      // only ever fire from the rarely-used "More actions" route. If the
      // tap already landed on an opponent, treat that AS the pick (no extra
      // step — the tap did double duty as location + victim); otherwise
      // defer to selectRecipient (skippable, "Sin víctima").
      if (selectedAction === "Foul") {
        if (isOpponent && targetPlayer) {
          const sent = dispatchEvent("Foul", "Standard", null, {
            location: selectedPlayerLocation ?? undefined,
            endLocation: destination.statsbomb,
            extraData: {
              fouled_player_id: targetPlayer.id,
              fouled_player_name: targetPlayer.full_name,
            },
          });
          if (sent) resetFlow();
          return {
            sent,
            outOfBounds: destination.isOutOfBounds,
            isGoal: false,
            triggerContext: null,
          };
        }
        setPendingOutcome("Standard");
        setCurrentStep("selectRecipient");
        return {
          sent: false,
          outOfBounds: destination.isOutOfBounds,
          isGoal: false,
          triggerContext: null,
        };
      }

      // ── Goal quick action pitch tap (EPIC-94, AC1) ──────────────────────
      // The Goal quick action's outcome is already decided the moment
      // "Gol" was pressed — this step (opened by
      // handleQuickActionSelect's default fallthrough for "Goal") only
      // captures WHERE ON THE PITCH the shot was taken from. Deliberately
      // does NOT fall into the generic Shot/Header destination-tap
      // derivation below (goal-mouth/GK/blocked outcome reinterpretation)
      // — that logic exists to DERIVE an on/off-target outcome from where
      // a shot's END location landed; a "Gol" tap is always a Goal and
      // only ever sets `location` (the shot's origin), never `end_location`
      // or `outcome`. Always tagged `data.location_source: "tap"` — the
      // visible "Omitir" control on this same step dispatches instead via
      // `handleOutcomeClick`'s own "Goal" branch (no tap, no tag, token
      // position kept, byte-identical to this quick action's old instant-
      // dispatch shape). The own-goal quick action ("OwnGoal",
      // `handleQuickActionSelect`) is a fully separate, unchanged instant-
      // dispatch branch and never reaches this step.
      if (selectedAction === "Goal") {
        // EPIC-96 (AC1, client wave 2026-09-24): `data.goal_quick_action:
        // true` is the dedicated marker `ActionStage.tsx`'s
        // `showGoalPlacementOverlay` gates the post-dispatch goal-frame
        // modal on — set here AND by the "Omitir" skip branch below
        // (`handleOutcomeClick`'s own "Goal" branch), never on `shot_type`
        // (this event's `shot_type` stays "Standard", read by penalty
        // counting/xG) and never on outcome alone (a generic Shot dispatch
        // also carries `outcome: "Goal"`, and PMA-COCK-208/216 deliberately
        // closed the live modal for that path).
        const sent = dispatchEvent("Goal", "Goal", null, {
          location: destination.statsbomb,
          extraData: { location_source: "tap", goal_quick_action: true },
        });
        if (sent) {
          resetFlow();
        }
        return {
          sent,
          outOfBounds: false,
          isGoal: true,
          triggerContext: null,
        };
      }

      const isPassOrShot =
        selectedAction === "Pass" ||
        selectedAction === "Shot" ||
        selectedAction === "Header";
      const ownGoalEdge =
        selectedPlayerSide === "home"
          ? "left"
          : selectedPlayerSide === "away"
            ? "right"
            : null;
      const isBehindOwnGoalLine =
        isPassOrShot &&
        destination.isOutOfBounds &&
        Boolean(destination.outOfBoundsEdge) &&
        ownGoalEdge !== null &&
        destination.outOfBoundsEdge === ownGoalEdge;
      const shouldAwardCorner = isBehindOwnGoalLine;

      // Pass destination guard:
      // - valid: teammate/opponent click OR out-of-bounds area
      // - invalid: empty inbounds grass click
      if (
        selectedAction === "Pass" &&
        !targetPlayer &&
        !destination.isOutOfBounds
      ) {
        return {
          sent: false,
          outOfBounds: false,
          isGoal: false,
          blockedInteriorPassDestination: true,
        };
      }

      const awardedCornerTeamId =
        shouldAwardCorner && selectedPlayerSide === "home"
          ? match?.away_team.id || null
          : shouldAwardCorner && selectedPlayerSide === "away"
            ? match?.home_team.id || null
            : null;
      const cornerReason = isBehindOwnGoalLine ? "behind_own_goal_line" : null;

      let outcome: string | null = null;
      const extraData: Record<string, any> = {
        destination_type: destination.isOutOfBounds
          ? "out_of_bounds"
          : targetPlayer
            ? isSameTeam
              ? "teammate"
              : "opponent"
            : "empty",
        out_of_bounds: destination.isOutOfBounds,
      };

      if (targetPlayer) {
        extraData.target_player_id = targetPlayer.id;
        extraData.target_player_name = targetPlayer.full_name;
      }

      // PMA-COCK-174: DirectShot dispatches through this same destination-
      // tap flow now (no more instant dispatch from handleQuickActionSelect)
      // — the shot_type tag moves here, at dispatch time, instead.
      if (selectedAction === "DirectShot") {
        extraData.shot_type = "Direct";
      }

      if (shouldAwardCorner) {
        extraData.corner_awarded = true;
        if (cornerReason) {
          extraData.corner_reason = cornerReason;
        }
        if (awardedCornerTeamId) {
          extraData.corner_team_id = awardedCornerTeamId;
        }
      }

      if (selectedAction === "Pass") {
        if (isBehindOwnGoalLine) {
          outcome = "Out";
        } else if (shouldAwardCorner) {
          outcome = "Incomplete";
        } else if (destination.isOutOfBounds) {
          outcome = "Out";
        } else if (isSameTeam) {
          outcome = "Complete";
        } else {
          outcome = "Incomplete";
          if (isOpponent && targetPlayer) {
            extraData.intercepted_by_id = targetPlayer.id;
            extraData.intercepted_by_name = targetPlayer.full_name;
          }
        }
      } else if (selectedAction === "DirectShot") {
        // PMA-COCK-176/177 (user decision 2026-09-09, overruling
        // PMA-COCK-174's on-dispatch OffTarget/GoalKick derivation):
        // DirectShot's destination tap is on-target-only. The opposing GK
        // under the tap is Saved (+ saved_by_*, now also a counted
        // goalkeeper save — see usePlayerStats.ts); the opposing outfield
        // player under the tap is Blocked (+ blocked_by_*, unchanged from
        // PMA-COCK-172); EVERYTHING else — goal mouth, open pitch, out of
        // bounds, the shooter's OWN goal mouth, a teammate tap — is
        // OnTarget. Never OffTarget, never a corner award (shouldAwardCorner
        // is already false for DirectShot — it is excluded from
        // isPassOrShot above), never the GoalKick trigger "Shot Out" used to
        // fire from this flow (see the triggerContext derivation below,
        // no longer DirectShot-scoped — "Shot Out" is its own restored
        // quick action with its own instant-dispatch trigger instead).
        // destination.goalMouth is deliberately not consulted here any
        // more — PMA-COCK-174's geometry/highlight stay as a visual aid
        // only (TacticalField.tsx untouched).
        if (isOpponent && targetPlayer?.position === "GK") {
          outcome = "Saved";
          if (targetPlayer) {
            extraData.saved_by_player_id = targetPlayer.id;
            extraData.saved_by_player_name = targetPlayer.full_name;
          }
        } else if (isOpponent) {
          outcome = "Blocked";
          if (targetPlayer) {
            extraData.blocked_by_player_id = targetPlayer.id;
            extraData.blocked_by_player_name = targetPlayer.full_name;
          }
        } else {
          outcome = "OnTarget";
        }
      } else if (selectedAction === "Shot" || selectedAction === "Header") {
        // PMA-COCK-172/174 generic "More actions" Shot/Header
        // destination-tap flow — untouched by PMA-COCK-176/177 (DirectShot
        // now has its own on-target-only branch above). EPIC-94 (AC1):
        // "Goal" no longer reaches this branch — it always returns early
        // above via its own dedicated pitch-tap branch, which never
        // reinterprets the outcome from where the tap landed (a "Gol" tap
        // only ever sets `location`, never derives on/off-target from
        // goal-mouth/GK/blocked geometry the way this Shot/Header flow
        // does). "Goal" was included in this condition before that change
        // landed, but was already unreachable here even then —
        // `handleQuickActionSelect`'s "Goal" branch dispatched instantly
        // and never advanced to `selectDestination` in the first place. A
        // tap in the goal-mouth zone at the OPPONENT's end (right for a
        // home shooter, left for away — mirrors ownGoalEdge above) is on
        // target.
        // destination.goalMouth is only ever set by TacticalField.tsx's
        // buildCoordinate (a pure-geometry open-pitch/out-of-bounds tap) —
        // a tap that lands ON a player node (GK/outfield credit below)
        // never carries it, see types.ts's FieldCoordinate.goalMouth doc
        // comment, so the GK/Blocked credit path is untouched.
        // shouldAwardCorner (a tap behind the SHOOTER'S OWN goal line)
        // still takes priority — unchanged.
        const opponentGoalEdge: FieldCoordinate["outOfBoundsEdge"] =
          selectedPlayerSide === "home"
            ? "right"
            : selectedPlayerSide === "away"
              ? "left"
              : null;
        if (shouldAwardCorner) {
          outcome = "OffTarget";
        } else if (
          destination.goalMouth &&
          destination.goalMouth === opponentGoalEdge
        ) {
          outcome = "OnTarget";
        } else if (destination.isOutOfBounds) {
          outcome = "OffTarget";
        } else if (isOpponent && targetPlayer?.position === "GK") {
          outcome = "Saved";
          if (targetPlayer) {
            extraData.saved_by_player_id = targetPlayer.id;
            extraData.saved_by_player_name = targetPlayer.full_name;
          }
        } else if (isOpponent) {
          outcome = "Blocked";
          if (targetPlayer) {
            extraData.blocked_by_player_id = targetPlayer.id;
            extraData.blocked_by_player_name = targetPlayer.full_name;
          }
        } else {
          outcome = "OffTarget";
        }
      } else if (selectedAction === "Duel") {
        outcome = isOpponent ? "Lost" : "Won";
      } else if (selectedAction === "Card") {
        outcome = null;
      }

      const recipient = shouldAwardCorner
        ? null
        : isSameTeam
          ? targetPlayer
          : null;
      const sent = dispatchEvent(selectedAction, outcome, recipient, {
        location: selectedPlayerLocation ?? undefined,
        endLocation: destination.statsbomb,
        extraData,
      });

      // Marker/destination out-of-bounds (corner or throw-in) never
      // auto-stops the clock or auto-creates a stoppage event — only the
      // explicit quick-action/referee buttons do that. The pass/shot event
      // above already logs the out-of-bounds outcome tag (and
      // `corner_awarded`/`corner_team_id` for the behind-own-goal-line case)
      // so the analytics categorization is unaffected.
      const isGoal = outcome === "Goal";

      // PMA-COCK-176/177: DirectShot never resolves to OffTarget any more
      // (see the on-target-only branch above), so the GoalKick trigger it
      // used to fire from this destination-tap flow (PMA-COCK-174,
      // `isDirectShotOffTarget`) is deleted outright, not merely
      // unreachable. "Shot Out" (restored, PMA-COCK-176) fires its own
      // GoalKick trigger from handleQuickActionSelect's instant-dispatch
      // branch instead, same as before PMA-COCK-174 retired it. The generic
      // "More actions" Shot flow's OffTarget destination tap still never
      // auto-triggers anything (PMA-COCK-172, unaffected).
      const triggerAction: IneffectiveAction | null = isGoal ? "Goal" : null;

      const triggerContext =
        sent && triggerAction
          ? {
              actionType: triggerAction,
              teamId: currentTeam.id,
              playerId: selectedPlayer.id,
            }
          : null;

      if (sent) {
        resetFlow();
      }

      return {
        sent,
        outOfBounds: destination.isOutOfBounds,
        isGoal,
        triggerContext,
      };
    },
    [
      match,
      currentTeam,
      dispatchEvent,
      resetFlow,
      resolvePlayerSide,
      selectedAction,
      selectedPlayer,
      selectedPlayerLocation,
    ],
  );

  const handleOutcomeClick = useCallback(
    (outcome: string) => {
      if (!selectedAction) return;
      const config = getActionConfig(selectedAction);
      console.log("[useActionFlow] handleOutcomeClick:", {
        outcome,
        selectedAction,
        config,
        needsRecipient: config?.needsRecipient,
      });
      if (selectedAction === "Pass" && outcome === "Out") {
        const sent = dispatchEvent(selectedAction, outcome, null, {
          location: selectedPlayerLocation ?? undefined,
          extraData: {
            destination_type: "out_of_bounds",
            out_of_bounds: true,
          },
        });
        if (sent && currentTeam && selectedPlayer) {
          onIneffectiveTrigger?.({
            note: "Out of bounds",
            teamId: resolveOpponentTeamId(currentTeam.id) || currentTeam.id,
            playerId: selectedPlayer.id,
            actionType: "ThrowIn", // pass out of bounds → opponent throw-in
          });
          resetFlow();
        }
        return;
      }
      if (selectedAction === "Shot" && outcome === "Goal") {
        const sent = dispatchEvent(selectedAction, outcome, null, {
          location: selectedPlayerLocation ?? undefined,
        });
        if (sent && currentTeam && selectedPlayer) {
          onIneffectiveTrigger?.({
            note: "Goal scored",
            teamId: currentTeam.id,
            playerId: selectedPlayer.id,
            actionType: "Goal",
          });
          resetFlow();
        }
        return;
      }
      // ── Goal quick action "Omitir" skip (EPIC-94, AC1) ──────────────────
      // The pitch-tap step (handleDestinationClick's own early "Goal"
      // branch) opens after the "Gol" quick action; a visible "Omitir"
      // control on that step calls `handleOutcomeSelect("Goal")`
      // (`ActionStage.tsx`, the SAME already-threaded prop the generic
      // Shot flow's own field-goal-btn uses above), which reaches here.
      // Dispatches immediately with the selected player's token position
      // — byte-for-byte the quick action's OLD instant-dispatch shape, no
      // `data.location_source` tag (that tag is tap-only). No
      // `onIneffectiveTrigger` call, matching the quick action's own prior
      // behavior (unlike the generic Shot+Goal branch just above, the
      // "Gol" quick action never triggered ineffective time and this
      // change does not add that).
      if (selectedAction === "Goal" && outcome === "Goal") {
        // EPIC-96 (AC1): same `data.goal_quick_action: true` marker as the
        // pitch-tap branch above (`handleDestinationClick`'s own "Goal"
        // branch) — Omitir still skips only the ORIGIN tap, not the
        // goal-frame modal that opens right after this dispatch confirms.
        const sent = dispatchEvent("Goal", "Goal", null, {
          location: selectedPlayerLocation ?? undefined,
          extraData: { goal_quick_action: true },
        });
        if (sent) {
          resetFlow();
        }
        return;
      }
      // ── PenaltyKick outcome — RETIRED (EPIC-98) ─────────────────────────
      // The old Anotado/Fallido two-button prompt (PMA-COCK-175) is gone:
      // "Tiro Penal" now opens its own goal-frame screen
      // (`PenaltyKickScreen.tsx`, rendered by `ActionStage.tsx` in place of
      // `OutcomeSelectionPanel` for this action, same waypoint pattern
      // Woodwork already established) whose four outcome buttons dispatch
      // directly through `handlePenaltyKickDispatch` below — never through
      // this generic outcome handler. `selectedAction === "PenaltyKick"`
      // can no longer reach `handleOutcomeClick` in practice; if it ever
      // does, it falls through to the same generic dispatch every
      // unmatched action reaches below, which is safe (see
      // `getActionConfig`) but will not carry the penalty-specific fields —
      // this is a defensive fallback, not a supported entry point.
      if (config?.needsRecipient) {
        setPendingOutcome(outcome);
        setCurrentStep("selectRecipient");
        console.log("[useActionFlow] Transitioning to selectRecipient step");
        return;
      }
      if (dispatchEvent(selectedAction, outcome, null)) {
        resetFlow();
      }
    },
    [
      dispatchEvent,
      currentTeam,
      onIneffectiveTrigger,
      resetFlow,
      selectedAction,
      selectedPlayer,
      selectedPlayerLocation,
      currentTeam?.players,
      resolveOpponentTeamId,
    ],
  );

  /**
   * Client wave 2026-09-19 ("Tiro al palo"): the operator's tap on one of
   * `WoodworkTargetSelector`'s three targets (`ActionStage.tsx`). One round
   * trip — outcome AND geometry dispatch together, no separate placement
   * step/modal: `outcome: "Post"` (an existing Shot outcome, already
   * counted under "off target" by `MatchAnalytics.tsx`'s
   * `getShotOutcomeCount(events, ["OffTarget", "Post"], true)`) plus
   * `data.goal_placement` set directly from `WOODWORK_TARGET_POINTS[zone]`
   * — `classifyPlacedShot` (goalFrame.ts) then reads that zone and
   * classifies the shot "woodwork" on the shot map with no further wiring.
   * No `onIneffectiveTrigger` call — unlike "Shot Out"/a missed penalty,
   * a woodwork hit does not reliably send the ball out of play (the
   * existing `STATIC_ACTION_PRIOR`/`"Shot|Post"` Markov row already
   * assumes play continues: Duel/Recovery/Clearance/Pass, no Goal Kick —
   * this reuses that existing assumption rather than inventing a new one).
   */
  const handleWoodworkPlace = useCallback(
    (zone: "post_left" | "crossbar" | "post_right") => {
      if (!currentTeam || !selectedPlayer) return;
      const point = WOODWORK_TARGET_POINTS[zone];
      const sent = dispatchEvent("Woodwork", "Post", null, {
        location: selectedPlayerLocation ?? undefined,
        extraData: {
          goal_placement: { ...point, zone, source: "live" as const },
        },
      });
      if (sent) {
        resetFlow();
      }
    },
    [
      currentTeam,
      selectedPlayer,
      selectedPlayerLocation,
      dispatchEvent,
      resetFlow,
    ],
  );

  /**
   * EPIC-98 ("Tiro Penal" goal-frame screen). The operator's confirm tap on
   * one of `PenaltyKickScreen.tsx`'s four outcome buttons — one round trip,
   * same shape as `handleWoodworkPlace` above: outcome AND (optional)
   * geometry dispatch together, no post-dispatch placement step. Bypasses
   * `dispatchEvent` deliberately (like the "OwnGoal" branch in
   * `handleQuickActionSelect` above) because the taker can differ from
   * `selectedPlayer` — the operator may have changed the pre-filled
   * taker on the screen before confirming, and `dispatchEvent` always reads
   * `selectedPlayer`/`currentTeam` off this hook's own state. Deliberately
   * does NOT call `triggerChipForAction` — the taker and the spot are both
   * already final by the time this fires, so there is nothing left for a
   * post-dispatch panel to correct; `ActionStage.tsx`'s
   * `showGoalPlacementOverlay` also drops its old `shot_type === "Penalty"`
   * branch for the same reason (dated reversal, see that file's own doc
   * comment) — belt-and-suspenders against a stray `chipState` match.
   *
   * Atajado's GK credit mirrors DirectShot's own tap-on-GK branch
   * (`handleDestinationClick`, PMA-COCK-176): the SAME two fields
   * (`saved_by_player_id`/`saved_by_player_name`), just resolved from the
   * opposing team's on-field roster instead of a tap, since there is no
   * destination tap in this flow to credit from. No opposing GK on the
   * field -> `Saved` with neither field set, per this UC's own decision.
   *
   * Ineffective-window behaviour matches each outcome's existing sibling
   * exactly: Gol fires the same "Goal" window a generic Shot->Goal does;
   * Fallado fires the same "GoalKick" window a missed shot does; Atajado
   * (mirrors DirectShot's own Saved branch) and Palo (mirrors
   * `handleWoodworkPlace` above) fire no window at all.
   */
  const handlePenaltyKickDispatch = useCallback(
    (params: {
      taker: Player;
      outcome: PenaltyKickOutcome;
      goalPlacement?: GoalPlacement;
    }) => {
      if (!match) return false;
      const side = resolvePlayerSide(params.taker);
      if (!side) return false;
      const takerTeam = side === "home" ? match.home_team : match.away_team;
      const opponentSide: "home" | "away" = side === "home" ? "away" : "home";
      const opponentTeam = side === "home" ? match.away_team : match.home_team;
      const penaltySpot =
        side === "away"
          ? PENALTY_SPOT_STATSBOMB.away
          : PENALTY_SPOT_STATSBOMB.home;

      const extraData: Record<string, any> = {
        shot_type: "Penalty",
        is_penalty: true,
      };
      if (params.goalPlacement) {
        extraData.goal_placement = params.goalPlacement;
      }
      if (params.outcome === "OffTarget") {
        extraData.destination_type = "out_of_bounds";
      }
      if (params.outcome === "Saved") {
        const onFieldOpponentIds = onFieldIds[opponentSide];
        const savingGoalkeeper = opponentTeam.players.find(
          (player) =>
            player.position === "GK" && onFieldOpponentIds.has(player.id),
        );
        if (savingGoalkeeper) {
          extraData.saved_by_player_id = savingGoalkeeper.id;
          extraData.saved_by_player_name = savingGoalkeeper.full_name;
        }
      }

      const payload = buildEventPayload(
        "PenaltyKick",
        params.outcome,
        null,
        takerTeam,
        params.taker,
        matchClock,
        operatorPeriod,
        penaltySpot,
        undefined,
        extraData,
        selectedZoneId,
      );
      sendEvent(payload);

      if (params.outcome === "Goal") {
        onIneffectiveTrigger?.({
          note: "Goal scored",
          teamId: takerTeam.id,
          playerId: params.taker.id,
          actionType: "Goal",
        });
      } else if (params.outcome === "OffTarget") {
        onIneffectiveTrigger?.({
          note: "Penalty missed",
          teamId: resolveOpponentTeamId(takerTeam.id) || takerTeam.id,
          playerId: params.taker.id,
          actionType: "GoalKick",
        });
      }

      resetFlow();
      return true;
    },
    [
      match,
      resolvePlayerSide,
      onFieldIds,
      matchClock,
      operatorPeriod,
      selectedZoneId,
      sendEvent,
      onIneffectiveTrigger,
      resolveOpponentTeamId,
      resetFlow,
    ],
  );

  const handleRecipientClick = useCallback(
    (recipient: Player) => {
      if (!selectedAction || !pendingOutcome) return;
      const isSkip = recipient.id === SKIP_RECIPIENT_ID;
      // Foul flow (PMA-COCK-166): recipient picked on the opposing roster is
      // the fouled player; "Sin víctima" (isSkip) still logs the foul, just
      // without a victim credit. The quick-action Penalty two-event flow
      // below is a SEPARATE branch (selectedAction === "Penalty") and is
      // untouched by this one (PMA-COCK-038).
      if (selectedAction === "Foul") {
        const sent = dispatchEvent(
          "Foul",
          pendingOutcome,
          null,
          isSkip
            ? undefined
            : {
                extraData: {
                  fouled_player_id: recipient.id,
                  fouled_player_name: recipient.full_name,
                },
              },
        );
        if (sent) resetFlow();
        return;
      }
      // Special case: Duel flow — send a separate event for the opponent player
      // on the opposing team and then reset.
      // DuelWon sets pendingOutcome="Lost", DuelLoss sets pendingOutcome="Won".
      if (
        selectedAction === "Duel" &&
        (pendingOutcome === "Lost" || pendingOutcome === "Won") &&
        match &&
        currentTeam
      ) {
        const opponentTeamId =
          resolveOpponentTeamId(currentTeam.id) || currentTeam.id;
        const opponentTeam =
          match.home_team.id === opponentTeamId
            ? match.home_team
            : match.away_team;
        const duelPayload = buildEventPayload(
          "Duel",
          pendingOutcome,
          null,
          opponentTeam,
          recipient,
          matchClock,
          operatorPeriod,
          selectedPlayerLocation ?? undefined,
          undefined,
          { opponent_id: selectedPlayer?.id },
          selectedZoneId,
        );
        sendEvent(duelPayload);
        resetFlow();
        return;
      }
      // Penalty flow — emit two events:
      // 1. FoulCommitted for the committer (counts as foul committed in stats)
      // 2. SetPiece/Penalty for the fouled player on opponent team (penalty received)
      if (
        selectedAction === "Penalty" &&
        match &&
        currentTeam &&
        selectedPlayer
      ) {
        const opponentTeamId =
          resolveOpponentTeamId(currentTeam.id) || currentTeam.id;
        const opponentTeam =
          match.home_team.id === opponentTeamId
            ? match.home_team
            : match.away_team;

        // 1. FoulCommitted event → committer's team & player
        const foulPayload = buildEventPayload(
          "Foul",
          "Penalty",
          null,
          currentTeam,
          selectedPlayer,
          matchClock,
          operatorPeriod,
          selectedPlayerLocation ?? undefined,
          undefined,
          {
            foul_type: "Standard",
            fouled_player_id: recipient.id,
            fouled_player_name: recipient.full_name,
          },
          selectedZoneId,
        );
        sendEvent(foulPayload);

        // 2. SetPiece/Penalty event → fouled player's team & player
        const penaltyPayload = buildEventPayload(
          "Penalty",
          "Complete",
          null,
          opponentTeam,
          recipient,
          matchClock,
          operatorPeriod,
          selectedPlayerLocation ?? undefined,
          undefined,
          {
            committed_by_id: selectedPlayer.id,
            committed_by_name: selectedPlayer.full_name,
          },
          selectedZoneId,
        );
        sendEvent(penaltyPayload);

        onIneffectiveTrigger?.({
          note: "Penalty",
          teamId: currentTeam.id,
          playerId: selectedPlayer.id,
          actionType: "FreeKick", // penalty foul → free kick category
        });
        // EPIC-98 (dated reversal of PMA-COCK-216 brief 13, client video
        // feedback 2026-09-26): the client reported that pressing "Tiro
        // Penal" only ever logged the foul ("Toca el jugador que recibió la
        // falta") with no way to record the kick itself. The two events
        // above (FoulCommitted + SetPiece/Penalty) stay BYTE-IDENTICAL to
        // the pre-merge award flow — this UC never touched their shape —
        // but the flow now ENDS here instead of continuing into the kick's
        // own waypoint: "Penal cometido" (this branch, the unchanged
        // `Penalty` action id) only ever awards the foul; "Tiro Penal" (the
        // `PenaltyKick` action id, its own wheel segment again, see
        // `constants.ts`) is a completely separate entry point where the
        // TAPPED player is the taker, not a value pre-filled from whoever
        // was fouled here — see `handleQuickActionSelect`'s own
        // `"PenaltyKick"` branch and `handlePenaltyKickDispatch` below.
        resetFlow();
        return;
      }
      if (dispatchEvent(selectedAction, pendingOutcome, recipient)) {
        resetFlow();
      }
    },
    [
      dispatchEvent,
      onIneffectiveTrigger,
      pendingOutcome,
      resetFlow,
      selectedAction,
      match,
      currentTeam,
      matchClock,
      operatorPeriod,
      resolveOpponentTeamId,
      selectedPlayer,
      selectedPlayerLocation,
      selectedZoneId,
      sendEvent,
    ],
  );

  useEffect(() => {
    currentStepRef.current = currentStep;
    console.log("[useActionFlow] currentStep changed to:", currentStep);
  }, [currentStep]);

  return {
    currentStep,
    currentTeam,
    selectedPlayer,
    selectedAction,
    attackedGoalMouthEdge,
    selectedZoneId,
    positionMode,
    setPositionMode: handlePositionModeChange,
    fieldAnchor,
    availableActions,
    availableOutcomes,
    handlePlayerClick,
    handleZoneSelect,
    handleQuickActionSelect,
    handleOpenMoreActions,
    handleDestinationClick,
    handleActionClick,
    handleOutcomeClick,
    handleRecipientClick,
    // Client wave 2026-09-19 ("Tiro al palo") — see the callback's own doc
    // comment above.
    handleWoodworkPlace,
    // EPIC-98 ("Tiro Penal" goal-frame screen) — see the callback's own doc
    // comment above.
    handlePenaltyKickDispatch,
    resetFlow,
    currentStepRef,
    chipState,
    handleChipTap,
    dismissChipPanel,
    handlePlacementTap,
    placedPlacement,
  } as const;
};
